import { useAuth0 } from '@auth0/auth0-react'
import { useMemo } from 'react'

import { CartFragment } from '../../../graphql/magento'
import useUserCategory from '../../../hooks/useUserCategory'
import { userCategories, UserCategory } from '../../../utils/userClaims'
import { useTestKitProducts } from '../../products/hooks/useTestKitProducts'
import { extractSpotlightKitItems, spotlightBlockMessage } from '../spotlight-guard'
import { requestRemoveCartItems, useRemoveCartItems } from '../state/cart-items/cart-items-remove'
import { RestrictedItem } from '../types'

export interface UseSpotlightPurchaseGate {
  /** Spotlight test-kit items currently in the cart */
  spotlightItems: RestrictedItem[]
  /** True when the signed-in account holds Spotlight kits it is not permitted to purchase */
  isBlocked: boolean
  /** User-facing explanation for the block */
  message: string
  /** Remove every Spotlight kit from the cart */
  handleRemoveSpotlightItems: () => void
  removeItemsError: unknown
  removeItemsLoading: boolean
}

// Account types allowed to purchase Spotlight kits (sc-139147). A patient only receives a kit via a
// practitioner's eScript, so patient category stands in for "linked to a practitioner"; everyone
// else (employees/HEs, other professionals) is blocked. Gated on account type, not the
// `purchase:test_kit` scope, which is practitioner-only and unrelated to the eScript flow.
const ELIGIBLE_CATEGORIES: ReadonlyArray<UserCategory> = [
  userCategories.patient,
  userCategories.practitioner,
]

/**
 * Guard preventing accounts that may not purchase Spotlight kits (e.g. DFH employees ordering via a
 * virtual-dispensary link) from checking out with one in their cart. Eligibility is by account type
 * (see `ELIGIBLE_CATEGORIES`); exposes the block state, message, and a bulk-remove. (sc-139147)
 *
 * No success callback is needed: `isBlocked` derives from the cart's items, so the notice clears
 * itself once the refreshed cart arrives.
 */
export const useSpotlightPurchaseGate = (
  customerCart: CartFragment | null | undefined,
): UseSpotlightPurchaseGate => {
  const { isAuthenticated, isLoading } = useAuth0()
  const { userCategory } = useUserCategory()
  const { isTestKitSku } = useTestKitProducts()
  const { loading: removeItemsLoading, result } = useRemoveCartItems()

  // memoized because it walks every cart line item, and because `handleRemoveSpotlightItems` below
  // closes over the array
  const spotlightItems = useMemo<RestrictedItem[]>(
    () => (customerCart ? extractSpotlightKitItems({ customerCart, isTestKitSku }) : []),
    [customerCart, isTestKitSku],
  )

  // Requiring a resolved category avoids briefly blocking an eligible account before Auth0 loads
  // the user, and scopes the guard to signed-in accounts — guests have no category and are handled
  // separately by checkout's available-actions logic.
  const isBlocked =
    isAuthenticated &&
    !isLoading &&
    userCategory !== undefined &&
    !ELIGIBLE_CATEGORIES.includes(userCategory) &&
    spotlightItems.length > 0

  return {
    spotlightItems,
    isBlocked,
    message: spotlightBlockMessage(userCategory),
    handleRemoveSpotlightItems: () => {
      if (customerCart) {
        requestRemoveCartItems({ items: spotlightItems })
      }
    },
    removeItemsError: result?._tag === 'Left' ? result.error : undefined,
    removeItemsLoading,
  }
}
